import axios from 'axios';
import { apiBaseUrl } from '../config';

/**
 * Singleton client for the market-data WebSocket.
 *
 * A singleton is not a stylistic choice. TickerBar mounts globally in App.js and
 * PaperTrading mounts StockChart, so a per-component socket would mean three
 * connections per browser tab and triple the server-side fan-out. Every consumer
 * shares this one connection and multiplexes over it.
 *
 * Connecting is two steps because a browser cannot set an Authorization header
 * on `new WebSocket()`: POST for a single-use 60s ticket, then open the socket
 * with it. The JWT never appears in a URL (and therefore never in a proxy log).
 *
 * Failure is designed to be a non-event: callers keep their existing REST
 * polling, and this only patches prices on top. If it never connects, the app
 * behaves exactly as it did before.
 */

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const DEGRADED_AFTER_FAILURES = 2;

function wsUrl(ticket) {
  // apiBaseUrl is http(s)://host:port/prefix — reuse it so we inherit the same
  // host, port and prefix rather than duplicating that assembly.
  const base = apiBaseUrl.replace(/^http/, 'ws');
  return `${base}/marketdata/stream?ticket=${encodeURIComponent(ticket)}`;
}

class MarketStream {
  constructor() {
    this.ws = null;
    this.token = null;
    this.status = 'idle';            // idle | connecting | open | closed | degraded
    this.degraded = false;
    this.failures = 0;
    this.reconnectTimer = null;
    this.intentionallyClosed = false;

    // symbol -> Set<callback>
    this.listeners = new Map();
    // Last tick per symbol, so a late subscriber gets a value immediately.
    this.latest = new Map();
    this.statusListeners = new Set();
  }

  setToken(token) {
    // A changed identity invalidates the current socket's ticket lineage.
    if (this.token && this.token !== token && this.ws) this.reconnect();
    this.token = token;
  }

  onStatus(fn) {
    this.statusListeners.add(fn);
    fn(this.getStatus());
    return () => this.statusListeners.delete(fn);
  }

  getStatus() {
    return { status: this.status, degraded: this.degraded, failures: this.failures };
  }

  emitStatus() {
    const s = this.getStatus();
    this.statusListeners.forEach((fn) => { try { fn(s); } catch { /* ignore */ } });
  }

  setStatus(status) {
    this.status = status;
    this.emitStatus();
  }

  /**
   * Subscribe to live prices.
   * @returns {() => void} unsubscribe
   */
  subscribe(symbols, callback) {
    const list = (symbols || []).map((s) => String(s).trim().toUpperCase()).filter(Boolean);
    if (list.length === 0) return () => {};

    for (const sym of list) {
      if (!this.listeners.has(sym)) this.listeners.set(sym, new Set());
      this.listeners.get(sym).add(callback);
    }

    // Seed synchronously from retained state so a newly mounted component does
    // not render an empty value while waiting for the next trade.
    const seed = {};
    for (const sym of list) {
      if (this.latest.has(sym)) seed[sym] = this.latest.get(sym);
    }
    if (Object.keys(seed).length) {
      try { callback(seed); } catch { /* ignore */ }
    }

    this.connect();
    this.sendSubscribe();

    return () => {
      for (const sym of list) {
        const set = this.listeners.get(sym);
        if (!set) continue;
        set.delete(callback);
        if (set.size === 0) this.listeners.delete(sym);
      }
      this.sendSubscribe();
    };
  }

  activeSymbols() {
    return [...this.listeners.keys()];
  }

  sendSubscribe() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const symbols = this.activeSymbols();
    // Server treats the message as additive; send the full set so the server's
    // view converges on ours after any reconnect.
    this.ws.send(JSON.stringify({ type: 'subscribe', symbols }));
  }

  async connect() {
    if (!this.token) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    if (this.status === 'connecting') return;

    this.intentionallyClosed = false;
    this.setStatus('connecting');

    let ticket;
    try {
      const res = await axios.post(
        `${apiBaseUrl}/marketdata/stream/ticket`,
        {},
        { headers: { Authorization: `Bearer ${this.token}` }, withCredentials: true }
      );
      ticket = res.data?.data?.ticket;
      if (!ticket) throw new Error('No ticket returned');
    } catch (err) {
      // 503 means the server has realtime disabled — do not keep retrying.
      if (err.response && err.response.status === 503) {
        this.setStatus('closed');
        this.degraded = true;
        this.emitStatus();
        return;
      }
      this.onFailure();
      return;
    }

    try {
      this.ws = new WebSocket(wsUrl(ticket));
    } catch {
      this.onFailure();
      return;
    }

    this.ws.onopen = () => {
      this.failures = 0;
      this.degraded = false;
      this.setStatus('open');
      this.sendSubscribe();
    };

    this.ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg.type !== 'tick' || !msg.data) return;
      this.dispatch(msg.data);
    };

    this.ws.onclose = () => {
      this.ws = null;
      if (this.intentionallyClosed) { this.setStatus('closed'); return; }
      this.onFailure();
    };

    this.ws.onerror = () => { /* onclose always follows; handled there */ };
  }

  dispatch(data) {
    for (const [symbol, tick] of Object.entries(data)) {
      this.latest.set(symbol, tick);
    }
    // Group by callback so a component watching 20 symbols gets ONE invocation
    // per frame rather than 20 setState calls.
    const byCallback = new Map();
    for (const [symbol, tick] of Object.entries(data)) {
      const set = this.listeners.get(symbol);
      if (!set) continue;
      for (const cb of set) {
        if (!byCallback.has(cb)) byCallback.set(cb, {});
        byCallback.get(cb)[symbol] = tick;
      }
    }
    for (const [cb, payload] of byCallback) {
      try { cb(payload); } catch { /* a bad consumer must not break the others */ }
    }
  }

  onFailure() {
    this.failures += 1;
    if (this.failures >= DEGRADED_AFTER_FAILURES) this.degraded = true;
    this.setStatus('degraded');
    this.scheduleReconnect();
  }

  scheduleReconnect() {
    if (this.reconnectTimer || this.intentionallyClosed) return;
    if (this.listeners.size === 0) return;   // nothing wants data

    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, Math.min(this.failures, 5))
    );
    // Jitter, so many tabs reconnecting after an outage do not arrive together.
    const jittered = delay * (0.5 + Math.random() * 0.5);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, jittered);
  }

  reconnect() {
    this.close({ silent: true });
    this.failures = 0;
    this.connect();
  }

  close({ silent = false } = {}) {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.ws) {
      try { this.ws.close(1000, 'Client closing'); } catch { /* ignore */ }
      this.ws = null;
    }
    if (!silent) this.setStatus('closed');
  }

  getLatest(symbol) {
    return this.latest.get(symbol) || null;
  }
}

export const marketStream = new MarketStream();
export { MarketStream };
export default marketStream;
